/** One thing a selector can offer: what it is called, and the id that is stored. */
export type TemplateChoice = {
  id: string;
  name: string;
};

/**
 * The options a selector offers: the page the list query returned, plus whatever is already chosen.
 *
 * The second argument is why this exists. A list query answers with a page, and a choice made
 * months ago can sit outside it — so a field drawn from the page alone would show a saved template
 * as empty and quietly clear it on the next save. The saved choice is resolved by id separately and
 * folded in here, which also keeps a GUID off the screen: a reader who cannot see the name cannot
 * tell whether the right thing is selected (issue #4830).
 *
 * Deduplicated by id, with the listed name winning, because the listed copy is the fresher read.
 * Sorted by name so a field's order does not change when a saved choice joins it.
 */
export function buildChoices(
  listed: readonly TemplateChoice[],
  saved: readonly TemplateChoice[],
): TemplateChoice[] {
  const byId = new Map<string, TemplateChoice>();
  for (const choice of saved) {
    if (choice.id) byId.set(choice.id, choice);
  }
  for (const choice of listed) {
    if (choice.id) byId.set(choice.id, choice);
  }
  return [...byId.values()].sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * What a chosen thing is called, or a description of it when nothing names it.
 *
 * A record can outlive what it points at — a template somebody archived and deleted, a document a
 * colleague removed. Saying so is the point: the configuration still holds the id and will still
 * fail to build a proposal from it, so the field has to show that something is chosen rather than
 * read as empty.
 */
export const UNNAMED_CHOICE = "No longer available";

export function nameOfChoice(choices: readonly TemplateChoice[], id: string): string {
  if (!id) return "";
  return choices.find((choice) => choice.id === id)?.name ?? UNNAMED_CHOICE;
}
